import PropTypes from 'prop-types';
import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Rebate from 'yoda-core-components/lib/assets/svg/Rebate.svg';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import DateFormatter from '../../dates/DateFormatter/DateFormatter';
import SiteComponent from '../SiteComponent/SiteComponent';
import getPreviewDate from '../../selectors/PreviewDateSelector';
import { selectFeatureFlags, isPreview } from '../../selectors/ContextSelector';
import actions from '../../actions/EcoRebatesAction';
import { initProductOptions } from '../../selectors/ProductOptionsSelector';
import { selectRebateInfoBySource } from '../../selectors/PdpNameSpaceSelector';

export class EcoRebates extends SiteComponent {
    static propTypes = {
        automationId: PropTypes.string,
        actions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        featureFlags: PropTypes.object,
        filteredItems: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        rebateInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        uiContext: PropTypes.string.isRequired,
        previewDate: PropTypes.string,
        fromMiniPDP: PropTypes.bool,
        isRebatesEligible: PropTypes.func,
        isPreviewEnv: PropTypes.bool,
    };

    static defaultProps = {
        automationId: '',
        actions: {},
        featureFlags: {},
        filteredItems: [],
        rebateInfo: {},
        previewDate: '',
        fromMiniPDP: false,
        isRebatesEligible: null,
        isPreviewEnv: false,
    };

    constructor(props) {
        super(props);
        this.itemsWithRebates = [];
        this.isRebateCalled = false;
    }

    componentDidMount() {
        /* istanbul ignore else */
        if (this.props.filteredItems) {
            const { filteredItems, uiContext, previewDate, isPreviewEnv } = this.props;
            this.itemsWithRebates = [];
            /* istanbul ignore next */
            filteredItems.forEach((item) => {
                if (item.rebatesAvailable) {
                    this.itemsWithRebates.push(item.id);
                }
            });

            /* istanbul ignore if */
            if (this.itemsWithRebates.length) {
                const ecoRebatePayload = {
                    itemsWithRebates: this.itemsWithRebates[0],
                    uiContext,
                    deliveryZipcode: this.props.deliveryZipcode,
                    sourceMiniPDP: this.props.fromMiniPDP,
                };
                if (isPreviewEnv) {
                    ecoRebatePayload.previewDate =
                        previewDate && typeof previewDate === 'object' && previewDate.format
                            ? DateFormatter.formatWithOptions(
                                  new Date(previewDate.format('MM.DD.YYYY')),
                                  '-'
                              )
                            : previewDate;
                }
                this.props.actions.getEcoRebates(ecoRebatePayload);
                this.isRebateCalled = true;
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        if (
            !isEqual(nextProps.filteredItems, this.props.filteredItems) ||
            nextProps.deliveryZipcode !== this.props.deliveryZipcode ||
            nextProps.previewDate !== this.props.previewDate
        ) {
            const { filteredItems, uiContext, previewDate } = nextProps;
            this.itemsWithRebates = [];
            filteredItems.forEach((item) => {
                if (item.rebatesAvailable) {
                    this.itemsWithRebates.push(item.id);
                }
            });
            let resPreviewDate = null;

            /* istanbul ignore next */
            if (this.props.isPreviewEnv) {
                resPreviewDate =
                    previewDate && typeof previewDate === 'object' && previewDate.format
                        ? DateFormatter.formatWithOptions(
                              new Date(previewDate.format('MM.DD.YYYY')),
                              '-'
                          )
                        : resPreviewDate;
            }
            if (this.itemsWithRebates.length && !this.isRebateCalled) {
                this.props.actions.getEcoRebates(
                    pickBy(
                        {
                            itemsWithRebates: this.itemsWithRebates[0],
                            uiContext,
                            deliveryZipcode: nextProps.deliveryZipcode,
                            previewDate: resPreviewDate,
                            sourceMiniPDP: this.props.fromMiniPDP,
                        },
                        identity
                    )
                );
                this.isRebateCalled = false;
            }
        }
    }

    shouldComponentUpdate(nextProps) {
        return (
            nextProps.rebateInfo !== this.props.rebateInfo ||
            nextProps.deliveryZipcode !== this.props.deliveryZipcode
        );
    }

    openRebatesModal = () => {
        this.props.actions.openEcoRebatesModal(this.props.fromMiniPDP);
    };

    /* istanbul ignore next */
    renderRebateInfo() {
        const { rebateInfo, isRebatesEligible } = this.props;
        const productRebateDetails = get(rebateInfo, 'EcoRebatesResponse.productRebateDetails', []);

        if (productRebateDetails.length) {
            return productRebateDetails.map((rebate, idx) => {
                const amount = get(rebate, 'maxRebate.amount', 0);
                isRebatesEligible && isRebatesEligible(!!amount);
                return amount > 0 ? (
                    <div
                        className={dt(['pt-[2px]'])}
                        data-automation-id={this.props.automationId}
                            key={`rebate-link-${idx}`} // eslint-disable-line
                    >
                        <button onClick={this.openRebatesModal}>
                            <Icon iconType="svg" width="25px" height="25px" viewBox="0 0 30 30">
                                <Rebate />
                            </Icon>
                            <span
                                className={dt([
                                    'leading-5',
                                    'pl-2',
                                    'text-sm',
                                    'text-black',
                                    'underline',
                                ])}
                            >
                                Up to ${amount} in Rebates Available
                            </span>
                        </button>
                    </div>
                ) : null;
            });
        }
        isRebatesEligible && isRebatesEligible(null);
        return null;
    }

    render() {
        return <> {this.renderRebateInfo()} </>;
    }
}

const mapStateToProps = (state, ownProps) => ({
    filteredItems: initProductOptions(ownProps.fromMiniPDP)(state).filteredItems,
    rebateInfo: selectRebateInfoBySource(ownProps.fromMiniPDP)(state),
    deliveryZipcode: state.deliveryZipcode,
    miniPDP: state.miniPDP,
    previewDate: state.context?.isPreview ? getPreviewDate(state) : null,
    featureFlags: selectFeatureFlags(state),
    isPreviewEnv: isPreview(state),
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(actions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(EcoRebates);
